import { Component, OnInit, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs';

import {
  SupportTicket,
  SupportTicketAttachment,
  SupportTicketDetails,
  SupportTicketStatus,
  SupportTicketType
} from 'src/app/models/support-ticket';

import { SupportTicketService } from 'src/app/services/support-ticket.service';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { UiBlockService } from 'src/app/services/ui-block.service';
import { UserSupportTicketComponent } from 'src/app/popup/user-support-ticket/user-support-ticket.component';
import { UtilsService } from 'src/app/services/utils.service';
import { EndpointsService } from 'src/app/services/endpoints.service';

@Component({
  selector: 'app-support-tickets',
  templateUrl: './support-tickets.component.html',
  styleUrls: ['../../app.component.scss', './support-tickets.component.scss'],
  standalone: false
})
export class SupportTicketsComponent implements OnInit, AfterViewChecked {

  tickets: SupportTicket[] = [];
  filteredTickets: SupportTicket[] = [];

  selectedTicketId?: number;
  selectedDetails?: SupportTicketDetails;

  loading = false;
  savingStatus = false;
  savingComment = false;
  savingAttachment = false;
  downloadingAttachmentIds = new Set<number>();

  showAllTickets = false;
  isAdmin = false;
  selectedStatus?: SupportTicketStatus;

  newComment = '';
  selectedFiles: File[] = [];

  readonly maxFiles = 5;
  readonly maxTotalSizeMb = 20;
  readonly ticketIdPrefix = 'GRT';
  readonly pageSize = 10;

  currentPage = 1;

  filters = {
    search: '',
    status: '',
    type: ''
  };

  statuses: SupportTicketStatus[] = [
    'NEW',
    'IN_PROGRESS',
    'RESOLVED',
    'ARCHIVED'

  ];

  ticketTypes: SupportTicketType[] = [
    'DEFECT',
    'IMPROVEMENT'
  ];

  @ViewChild('commentList') commentList?: ElementRef<HTMLDivElement>;

  private shouldScrollCommentsToBottom = false;

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private supportTicketService: SupportTicketService,
    private modalControlService: ModalControlService,
    private uiBlockService: UiBlockService,
    private endpointService: EndpointsService,
    private utilsService: UtilsService

  ) { }

  ngOnInit(): void {
    const rolesToken = this.endpointService.getUserRolesToken();

    this.isAdmin = rolesToken
    ? UtilsService.getIsAdminFromJwtToken(rolesToken)
    : false;

    this.showAllTickets = this.isAdmin;
    this.loadTickets();
  }

  ngAfterViewChecked(): void {
    if (!this.shouldScrollCommentsToBottom) {
      return;
    }

    this.scrollCommentsToBottom();
    this.shouldScrollCommentsToBottom = false;
  }

  loadTickets(): void {
    this.loading = true;
    this.uiBlockService.startBlock('Loading tickets...');

    this.supportTicketService.getTickets(this.showAllTickets)
      .pipe(finalize(() => {
        this.loading = false;
        this.uiBlockService.stopBlock();
      }))
      .subscribe({
        next: tickets => {
          this.tickets = tickets || [];
          this.applyFilters();

           if (this.selectedTicketId) {
            this.loadTicketDetails(this.selectedTicketId);
            return;
          }

        },
        error: error => {
          console.error('Error loading support tickets', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error loading support tickets.'
          );
        }
      });
  }

  get pagedTickets(): SupportTicket[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredTickets.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredTickets.length / this.pageSize));
  }

  get pageStart(): number {
    if (this.filteredTickets.length === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredTickets.length);
  }

  applyFilters(resetPage = true): void {
    const search = this.filters.search.trim().toLowerCase();
    const status = this.filters.status;
    const type = this.filters.type;

    this.filteredTickets = this.tickets.filter(ticket => {
      const ticketReference = this.getTicketDisplayId(ticket.id).toLowerCase();
      const ticketIdText = String(ticket.id ?? '').toLowerCase();

      const matchesSearch =
        !search ||
        ticket.title?.toLowerCase().includes(search) ||
        ticket.description?.toLowerCase().includes(search) ||
        ticket.createdBy?.toLowerCase().includes(search) ||
        ticketReference.includes(search) ||
        ticketIdText.includes(search);

      const matchesStatus =
        !status || ticket.status === status;

      const matchesType =
        !type || ticket.ticketType === type;

      return matchesSearch && matchesStatus && matchesType;
    });

    if (resetPage) {
      this.currentPage = 1;
    }

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  selectTicket(ticket: SupportTicket): void {
    if (!ticket.id) {
      return;
    }

    this.selectedTicketId = ticket.id;
    this.loadTicketDetails(ticket.id);
  }

  backToList(): void {
    this.selectedTicketId = undefined;
    this.selectedDetails = undefined;
    this.selectedStatus = undefined;
    this.newComment = '';
    this.selectedFiles = [];
  }

  previousPage(): void {
    if (this.currentPage <= 1) {
      return;
    }

    this.currentPage--;
  }

  nextPage(): void {
    if (this.currentPage >= this.totalPages) {
      return;
    }

    this.currentPage++;
  }

  loadTicketDetails(ticketId: number): void {
    this.supportTicketService.getTicketDetails(ticketId)
      .subscribe({
        next: details => {
          this.selectedDetails = details;
          this.selectedStatus = details.ticket.status;
          this.newComment = '';
          this.selectedFiles = [];
          this.shouldScrollCommentsToBottom = true;
        },
        error: error => {
          console.error('Error loading ticket details', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error loading ticket details.'
          );
        }
      });
  }

  openNewTicket(): void {
    const modalRef = this.modalService.show(UserSupportTicketComponent, {
      backdrop: 'static'
    });

    modalRef.content?.created?.subscribe((createdTicket: SupportTicket) => {
      modalRef.hide();
      this.loadTickets();

      if (createdTicket?.id) {
        this.selectedTicketId = createdTicket.id;
        this.loadTicketDetails(createdTicket.id);
      }
    });
  }

  updateStatus(status: SupportTicketStatus): void {
    if (!this.isAdmin || !this.selectedDetails?.ticket.id || this.selectedDetails.ticket.status === status) {
      return;
    }

    this.savingStatus = true;

    this.supportTicketService.updateTicketStatus(this.selectedDetails.ticket.id, {
      status
    })
      .pipe(finalize(() => this.savingStatus = false))
      .subscribe({
        next: updatedTicket => {
          this.selectedDetails!.ticket = updatedTicket;
          this.selectedStatus = updatedTicket.status;
          this.replaceTicketInList(updatedTicket);
        },
        error: error => {
          console.error('Error updating ticket status', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error updating the ticket status.'
          );
        }
      });
  }

  onStatusChange(status: SupportTicketStatus): void {
    this.selectedStatus = status;
  }

  saveStatusChange(): void {
    if (!this.selectedStatus) {
      return;
    }

    this.updateStatus(this.selectedStatus);
  }

  hasPendingStatusChange(): boolean {
    return !!this.selectedStatus && this.selectedStatus !== this.selectedDetails?.ticket.status;
  }

  saveJiraFlag(): void {
    if (!this.selectedDetails?.ticket.id || !this.selectedDetails.ticket.status) {
      return;
    }

    this.savingStatus = true;

    this.supportTicketService.updateTicketStatus(this.selectedDetails.ticket.id, {
      status: this.selectedDetails.ticket.status,
      exportToJira: this.selectedDetails.ticket.exportToJira
    })
      .pipe(finalize(() => this.savingStatus = false))
      .subscribe({
        next: updatedTicket => {
          this.selectedDetails!.ticket = updatedTicket;
          this.replaceTicketInList(updatedTicket);
        },
        error: error => {
          console.error('Error updating Jira flag', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error updating the Jira export flag.'
          );
        }
      });
  }

  addComment(): void {
    if (!this.selectedTicketId || !this.newComment.trim() || this.isReadOnlyStatus(this.selectedDetails?.ticket.status)) {
      return;
    }

    this.savingComment = true;

    this.supportTicketService.addComment(this.selectedTicketId, this.newComment.trim())
      .pipe(finalize(() => this.savingComment = false))
      .subscribe({
        next: comment => {
          this.selectedDetails?.comments.push(comment);
          this.newComment = '';
          this.shouldScrollCommentsToBottom = true;
        },
        error: error => {
          console.error('Error adding comment', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error adding the comment.'
          );
        }
      });
  }

  onFilesSelected(event: Event): void {
    if (this.isReadOnlyStatus(this.selectedDetails?.ticket.status)) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    if (files.length > this.maxFiles) {
      this.modalControlService.showMesssageModal(
        'Support Tickets',
        `Maximum ${this.maxFiles} files allowed.`
      );
      input.value = '';
      return;
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxBytes = this.maxTotalSizeMb * 1024 * 1024;

    if (totalSize > maxBytes) {
      this.modalControlService.showMesssageModal(
        'Support Tickets',
        `Maximum total attachment size is ${this.maxTotalSizeMb} MB.`
      );
      input.value = '';
      return;
    }

    this.selectedFiles = files;
  }

  uploadAttachments(): void {
    if (!this.selectedTicketId || this.selectedFiles.length === 0 || this.isReadOnlyStatus(this.selectedDetails?.ticket.status)) {
      return;
    }

    this.savingAttachment = true;

    this.supportTicketService.addAttachments(this.selectedTicketId, this.selectedFiles)
      .pipe(finalize(() => this.savingAttachment = false))
      .subscribe({
        next: attachments => {
          this.selectedDetails?.attachments.push(...attachments);
          this.selectedFiles = [];
        },
        error: error => {
          console.error('Error uploading attachments', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error uploading the attachments.'
          );
        }
      });
  }

  removeAttachmentFromTicket(attachmentId?: number): void {
    if (!this.selectedTicketId || !attachmentId || this.isReadOnlyStatus(this.selectedDetails?.ticket.status)) {
      return;
    }

    this.savingAttachment = true;

    this.supportTicketService.removeAttachment(this.selectedTicketId, attachmentId)
      .pipe(finalize(() => this.savingAttachment = false))
      .subscribe({
        next: () => {
          if (!this.selectedDetails) {
            return;
          }

          this.selectedDetails.attachments = this.selectedDetails.attachments
            .filter((attachment) => attachment.id !== attachmentId);
        },
        error: (error) => {
          console.error('Error removing attachment', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error removing the attachment.'
          );
        }
      });
  }

  downloadAttachmentFromTicket(attachment: SupportTicketAttachment): void {
    if (!this.selectedTicketId || !attachment.id) {
      return;
    }

    const attachmentId = attachment.id;
    const fileName = attachment.fileName;

    this.downloadingAttachmentIds.add(attachmentId);

    this.supportTicketService.downloadAttachment(this.selectedTicketId, attachmentId)
      .pipe(finalize(() => this.downloadingAttachmentIds.delete(attachmentId)))
      .subscribe({
        next: (file) => {
          this.downloadFile(file, fileName);
        },
        error: (error) => {
          console.error('Error downloading attachment', error);
          this.modalControlService.showMesssageModal(
            'Support Tickets',
            'There was an error downloading the attachment.'
          );
        }
      });
  }

  isDownloadingAttachment(attachmentId?: number): boolean {
    return !!attachmentId && this.downloadingAttachmentIds.has(attachmentId);
  }

  toggleAllTickets(): void {
    if (!this.isAdmin) {
      return;
    }

    this.showAllTickets = !this.showAllTickets;
    this.selectedTicketId = undefined;
    this.selectedDetails = undefined;
    this.loadTickets();
  }

  close(): void {
    this.bsModalRef.hide();
  }

  getStatusLabel(status?: string): string {
    return (status || 'UNKNOWN').replace(/_/g, ' ');
  }

  getTicketDisplayId(ticketId?: number): string {
    return ticketId ? `#${this.ticketIdPrefix}${ticketId}` : '';
  }

  isReadOnlyStatus(status?: string): boolean {
    return status === 'RESOLVED' || status === 'ARCHIVED';
  }

  getStatusClass(status?: string): string {
    return `status-${status || 'UNKNOWN'}`;
  }

  formatFileSize(size?: number | null): string {
    if (!size && size !== 0) {
      return '-';
    }

    if (size < 1024) {
      return `${size} B`;
    }

    if (size < 1024 * 1024) {
      return `${Math.round(size / 1024)} KB`;
    }

    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  trackByTicketId(index: number, ticket: SupportTicket): number {
    return ticket.id ?? index;
  }

  private replaceTicketInList(updatedTicket: SupportTicket): void {
    this.tickets = this.tickets.map(ticket =>
      ticket.id === updatedTicket.id ? updatedTicket : ticket
    );
    this.applyFilters(false);
  }
  getTicketTypeLabel(ticketType?: string): string {
  return (ticketType || 'DEFECT').replace(/_/g, ' ');
}

  private scrollCommentsToBottom(): void {
    const element = this.commentList?.nativeElement;

    if (!element) {
      return;
    }

    element.scrollTop = element.scrollHeight;
  }

  private downloadFile(file: Blob, fileName: string): void {
    const objectUrl = window.URL.createObjectURL(file);
    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = fileName || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(objectUrl);
  }
}
